import { useState, useCallback, useMemo } from "react";
import {
  useSensors,
  useSensor,
  TouchSensor,
  MouseSensor,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";

export const SENSOR_CONFIG = {
  activationConstraint: {
    delay: 500,
    tolerance: 8,
    distance: 8,
  },
};

export const useDragAndDrop = <T>(
  itemsToDrag: T[],
  getId: (item: T) => string,
  moveItem: (oldIndex: number, newIndex: number) => void,
  setSwipedId: (id: string | null) => void
) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(TouchSensor, SENSOR_CONFIG),
    useSensor(MouseSensor, SENSOR_CONFIG)
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = itemsToDrag.findIndex((item) => getId(item) === active.id);
        const newIndex = itemsToDrag.findIndex((item) => getId(item) === over?.id);
        moveItem(oldIndex, newIndex);
      }

      setActiveId(null);
      setSwipedId(null);
    },
    [setSwipedId, itemsToDrag, moveItem, getId]
  );

  const sortableItems = useMemo(() => itemsToDrag.map((item) => getId(item)), [getId, itemsToDrag]);

  const activeMenuList = useMemo(
    () => itemsToDrag.find((list) => getId(list) === activeId),
    [itemsToDrag, getId, activeId]
  );

  return {
    activeId,
    sensors,
    handleDragStart,
    handleDragEnd,
    sortableItems,
    activeMenuList,
  };
};
